<template>
  <HeaderComponent />
  
  <HeroComponent 
    image="https://images.pexels.com/photos/5428833/pexels-photo-5428833.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
    text="Frequently Asked Questions" 
    subtitle="Find answers to common questions about our services and process"
  />

  <!-- FAQ Section -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto">
        

        <!-- Loading State -->
        <div v-if="isLoading" class="py-20">
          <LoadingRipple message="Loading FAQs..." />
        </div>

        <!-- Empty State -->
        <div v-else-if="faqs.length === 0" class="text-center py-16" data-sal="slide-up" data-sal-delay="400">
          <p class="text-gray-600">No FAQs available.</p>
        </div>

        <!-- FAQ Sections -->
        <div v-else class="space-y-12" data-sal="slide-up" data-sal-delay="400">
          <div
            v-for="category in categoriesWithFAQs"
            :key="category"
            class="space-y-4"
          >
            <!-- Section Header -->
            <div class="text-center mb-8">
              <h2 class="text-3xl font-bold font-heading text-gray-900 mb-2">{{ category }}</h2>
              <div class="w-24 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            
            <!-- FAQ Items for this category -->
            <div class="space-y-4">
              <div
                v-for="(faq, faqIndex) in getFAQsForCategory(category)"
                :key="`${category}-${faqIndex}`"
                class="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                <button
                  @click="toggleFAQ(`${category}-${faqIndex}`)"
                  class="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-300"
                >
                  <h4 class="text-base font-semibold text-gray-900 pr-4">{{ faq.question }}</h4>
                  <div class="flex-shrink-0">
                    <svg 
                      :class="[
                        'w-6 h-6 text-primary transition-transform duration-300',
                        openFAQs.includes(`${category}-${faqIndex}`) ? 'transform rotate-180' : ''
                      ]"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </button>
                
                <transition
                  name="faq-slide"
                  @enter="enter"
                  @leave="leave"
                >
                  <div v-if="openFAQs.includes(`${category}-${faqIndex}`)" class="px-8 pb-6">
                    <div class="text-gray-600 leading-relaxed" v-html="faq.answer"></div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact CTA -->
        <div class="text-center mt-16 bg-white rounded-3xl p-8 shadow-lg" data-sal="slide-up" data-sal-delay="600">
          <h3 class="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h3>
          <p class="text-gray-600 mb-6">
            Can't find the answer you're looking for? Our team is here to help you with any questions or concerns.
          </p>
          <router-link 
            to="/contact" 
            class="inline-block bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Contact Us
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <FooterComponent />
</template>

<script>
import HeaderComponent from '@/components/Header.vue'
import FooterComponent from '@/components/Footer.vue'
import HeroComponent from '@/components/Hero.vue'
import LoadingRipple from '@/components/LoadingRipple.vue'

export default {
  name: 'FAQView',
  components: {
    HeaderComponent,
    FooterComponent,
    HeroComponent,
    LoadingRipple
  },
  data() {
    return {
      openFAQs: [],
      categories: [],
      faqs: [],
      isLoading: true
    };
  },
  computed: {
    categoriesWithFAQs() {
      const categorySet = new Set();
      this.faqs.forEach(faq => {
        if (faq.category) {
          categorySet.add(faq.category);
        }
      });
      return Array.from(categorySet).sort();
    }
  },
  async mounted() {
    await this.loadFAQs();
    this.initializeScrollAnimations();
  },
  methods: {
    async loadFAQs() {
      try {
        this.isLoading = true;
        
        // Try to load from NocoDB first
        try {
          const nocoService = (await import('@/services/nocodb.js')).default;
          const data = await nocoService.getFAQs();
          
          if (data && data.list && data.list.length > 0) {
            this.faqs = data.list;
          }
        } catch (nocoError) {
          // Silently handle NocoDB connection issues
          console.warn('NocoDB connection failed, no fallback data available');
        }
        
      } catch (error) {
        console.error('Error loading FAQs:', error);
      } finally {
        this.isLoading = false;
      }
    },
    initializeScrollAnimations() {
      this.$nextTick(() => {
        if (typeof sal !== 'undefined') {
          sal({
            duration: 500,
            threshold: 0.0001,
            once: true
          });
        }
      });
    },
    getFAQsForCategory(category) {
      return this.faqs.filter(faq => faq.category === category);
    },
    toggleFAQ(index) {
      const faqIndex = this.openFAQs.indexOf(index);
      if (faqIndex > -1) {
        this.openFAQs.splice(faqIndex, 1);
      } else {
        this.openFAQs.push(index);
      }
    },
    enter(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.offsetHeight; // force reflow
      el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      el.style.height = el.scrollHeight + 'px';
      el.style.opacity = '1';
    },
    leave(el) {
      el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      el.style.height = '0';
      el.style.opacity = '0';
    }
  }
}
</script>

<style scoped>
.faq-slide-enter-active, .faq-slide-leave-active {
  overflow: hidden;
}
</style>